import React, { createContext, useState, useEffect, useCallback, ReactNode, useContext, useMemo } from 'react';
import * as signalR from '@microsoft/signalr';
import { mailApi } from '../services/mailApi';
import { documentSharingApi } from '../services/documentSharingApi';
import { MailMessage, MailStatus, SendMailRequest } from '../types/mail';
import { ShareType } from '../types/documentSharing';
import { MailMessageDto } from '../models/MailMessageDto';
import { toMailMessage } from '../utils/mailMapper';
import { API_URL } from '../config/api';
import { useAuth } from './AuthContext';
import { ShareDocumentRequest } from '../types/documentSharing';

const PAGE_SIZE = 8;

interface MailContextType {
  incomingMails: MailMessage[];
  outgoingMails: MailMessage[];
  unreadCount: number;
  isLoading: boolean;
  hasMoreIncoming: boolean;
  hasMoreOutgoing: boolean;
  isLoadingMoreIncoming: boolean;
  isLoadingMoreOutgoing: boolean;
  sendMail: (recipientUsername: string, knowledgeItemId: string, text?: string, shareType?: string) => Promise<MailMessage>;
  acceptMail: (mailId: string) => Promise<void>;
  rejectMail: (mailId: string) => Promise<void>;
  loadMoreMail: (type: 'incoming' | 'outgoing') => Promise<void>;
}

export const MailContext = createContext<MailContextType | undefined>(undefined);

export const MailProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, isAuthenticated, token } = useAuth();
  const [incomingMails, setIncomingMails] = useState<MailMessage[]>([]);
  const [outgoingMails, setOutgoingMails] = useState<MailMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [incomingPage, setIncomingPage] = useState(1);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [hasMoreIncoming, setHasMoreIncoming] = useState(true);
  const [hasMoreOutgoing, setHasMoreOutgoing] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreIncoming, setIsLoadingMoreIncoming] = useState(false);
  const [isLoadingMoreOutgoing, setIsLoadingMoreOutgoing] = useState(false);

  const [isConnected, setIsConnected] = useState(false);
  const [hubConnection, setHubConnection] = useState<signalR.HubConnection | null>(null);

  const loadInitialMails = useCallback(async () => {
    if (!isAuthenticated || !profile) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [incomingResponse, outgoingResponse] = await Promise.all([
        mailApi.getIncomingMails(1, PAGE_SIZE),
        mailApi.getOutgoingMails(1, PAGE_SIZE)
      ]);

      setIncomingMails(incomingResponse.items);
      setOutgoingMails(outgoingResponse.items);
      setUnreadCount(incomingResponse.items.filter(mail => mail.status === MailStatus.Sent).length);
      setHasMoreIncoming(incomingResponse.hasNextPage);
      setHasMoreOutgoing(outgoingResponse.hasNextPage);
      setIncomingPage(1);
      setOutgoingPage(1);
    } catch (error) {
      console.error('[MailContext] Ошибка при загрузке почты:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, profile]);

  const loadMoreMail = useCallback(async (type: 'incoming' | 'outgoing') => {
    if (type === 'incoming') {
      if (isLoadingMoreIncoming || !hasMoreIncoming) return;
      
      setIsLoadingMoreIncoming(true);
      try {
        const nextPage = incomingPage + 1;
        const response = await mailApi.getIncomingMails(nextPage, PAGE_SIZE);
        setIncomingMails(prev => [...prev, ...response.items]);
        setHasMoreIncoming(response.hasNextPage);
        setIncomingPage(nextPage);
      } catch (error) {
        console.error('Ошибка при загрузке входящих писем:', error);
      } finally {
        setIsLoadingMoreIncoming(false);
      }
    } else {
      if (isLoadingMoreOutgoing || !hasMoreOutgoing) return;
      
      setIsLoadingMoreOutgoing(true);
      try {
        const nextPage = outgoingPage + 1;
        const response = await mailApi.getOutgoingMails(nextPage, PAGE_SIZE);
        setOutgoingMails(prev => [...prev, ...response.items]);
        setHasMoreOutgoing(response.hasNextPage);
        setOutgoingPage(nextPage);
      } catch (error) {
        console.error('Ошибка при загрузке исходящих писем:', error);
      } finally {
        setIsLoadingMoreOutgoing(false);
      }
    }
  }, [
    incomingPage,
    outgoingPage,
    hasMoreIncoming,
    hasMoreOutgoing,
    isLoadingMoreIncoming,
    isLoadingMoreOutgoing
  ]);

  useEffect(() => {
    loadInitialMails();
  }, [loadInitialMails]);

  // SignalR connection
  useEffect(() => {
    if (!isAuthenticated || !profile) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_URL.replace('/api', '')}/mailHub`, {
        accessTokenFactory: () => token || '',
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Обработчики событий подключения
    connection.onclose((error) => {
      console.log('MailHub connection closed:', error);
      setIsConnected(false);
    });

    connection.onreconnecting((error) => {
      console.log('MailHub reconnecting:', error);
      setIsConnected(false);
    });

    connection.onreconnected((connectionId) => {
      console.log('MailHub reconnected:', connectionId);
      setIsConnected(true);
    });

    const startConnection = async () => {
      try {
        console.log('Starting MailHub connection...');
        await connection.start();
        console.log('MailHub connection started successfully');
        setIsConnected(true);
        setHubConnection(connection);

        // Присоединяемся к группе пользователя
        await connection.invoke('JoinUserGroup');
        console.log('Joined user group for mail notifications');

        // Обработчики событий
        connection.on('NewMailReceived', (mailDto: MailMessageDto) => {
          console.log('📧 [MailContext] Получено новое письмо:', mailDto);
          const newMail = toMailMessage(mailDto);
          setIncomingMails(prev => [newMail, ...prev]);
          setUnreadCount(prev => prev + 1);
        });

        connection.on('UnreadCountUpdated', (count: number) => {
          console.log('📊 [MailContext] Обновлен счетчик непрочитанных:', count);
          setUnreadCount(count);
        });

        connection.on('MailStatusUpdated', (mailDto: MailMessageDto) => {
          console.log('🔄 [MailContext] Статус письма изменен:', mailDto);
          const updatedMail = toMailMessage(mailDto);
          
          // Обновляем письмо в соответствующем списке
          setIncomingMails(prev => prev.map(mail => 
            mail.id === updatedMail.id ? updatedMail : mail
          ));
          
          setOutgoingMails(prev => prev.map(mail => 
            mail.id === updatedMail.id ? updatedMail : mail
          ));
          
          // Пересчитываем счетчик непрочитанных
          setUnreadCount(prev => {
            const newCount = prev + (updatedMail.status === MailStatus.Sent ? 1 : 
                                   updatedMail.status === MailStatus.Accepted || updatedMail.status === MailStatus.Declined ? -1 : 0);
            return Math.max(0, newCount);
          });
        });

      } catch (error) {
        console.error('Ошибка подключения к SignalR:', error);
        setIsConnected(false);
      }
    };

    startConnection();

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [isAuthenticated, profile, token]);

  // Функции для взаимодействия с почтой
  const sendMail = useCallback(async (recipientUsername: string, knowledgeItemId: string, text?: string, shareType?: string) => {
    try {
      // Если это ShareAccess, используем новый API для совместного доступа
      if (shareType === 'ShareAccess') {
        const request: ShareDocumentRequest = {
          recipientUsername,
          shareType: ShareType.ShareAccess,
          text,
          knowledgeItemId
        };
        const response = await documentSharingApi.shareDocument(request);
        setOutgoingMails(prev => [response, ...prev]);
        return response;
      } else {
        // Для Send используем старый API
        const request: SendMailRequest = {
          recipientUsername,
          knowledgeItemId,
          text,
          shareType
        };
        const response = await mailApi.sendMail(request);
        setOutgoingMails(prev => [response, ...prev]);
        return response;
      }
    } catch (error: any) {
      console.error('Ошибка при отправке письма:', error);
      const data = error?.response?.data;
      const status = error?.response?.status;
      const problemTitle = data?.title || data?.message;
      const friendly = problemTitle
        || (status === 403 ? 'Доступ запрещён' : status === 401 ? 'Пользователь не авторизован' : 'Произошла ошибка. Попробуйте позже.');
      throw new Error(friendly);
    }
  }, []);

  const acceptMail = useCallback(async (mailId: string) => {
    try {
      await mailApi.acceptMail(mailId);
      setIncomingMails(prev => prev.map(mail => 
        mail.id === mailId ? { ...mail, status: MailStatus.Accepted } : mail
      ));
      setUnreadCount(prev => prev - 1);
    } catch (error: any) {
      console.error('Ошибка при принятии письма:', error);
      const data = error?.response?.data;
      const status = error?.response?.status;
      const problemTitle = data?.title || data?.message;
      const friendly = problemTitle
        || (status === 403 ? 'Доступ запрещён' : status === 401 ? 'Пользователь не авторизован' : 'Произошла ошибка. Попробуйте позже.');
      throw new Error(friendly);
    }
  }, []);

  const rejectMail = useCallback(async (mailId: string) => {
    try {
      await mailApi.rejectMail(mailId);
      setIncomingMails(prev => prev.map(mail => 
        mail.id === mailId ? { ...mail, status: MailStatus.Declined } : mail
      ));
      setUnreadCount(prev => prev - 1);
    } catch (error: any) {
      console.error('Ошибка при отклонении письма:', error);
      const data = error?.response?.data;
      const status = error?.response?.status;
      const problemTitle = data?.title || data?.message;
      const friendly = problemTitle
        || (status === 403 ? 'Доступ запрещён' : status === 401 ? 'Пользователь не авторизован' : 'Произошла ошибка. Попробуйте позже.');
      throw new Error(friendly);
    }
  }, []);

  const value = useMemo(() => ({
    incomingMails,
    outgoingMails,
    unreadCount,
    isLoading,
    isLoadingMoreIncoming,
    isLoadingMoreOutgoing,
    hasMoreIncoming,
    hasMoreOutgoing,
    sendMail,
    acceptMail,
    rejectMail,
    loadMoreMail,
  }), [
    incomingMails,
    outgoingMails,
    unreadCount,
    isLoading,
    isLoadingMoreIncoming,
    isLoadingMoreOutgoing,
    hasMoreIncoming,
    hasMoreOutgoing,
    sendMail,
    acceptMail,
    rejectMail,
    loadMoreMail
  ]);

  return (
    <MailContext.Provider value={value}>
      {children}
    </MailContext.Provider>
  );
};

export const useMailContext = () => {
  const context = useContext(MailContext);
  if (context === undefined) {
    throw new Error('useMailContext must be used within a MailProvider');
  }
  return context;
};
